import React, { useState, useEffect } from "react";
import { useAppSelector, useAppDispatch, store } from "../../../../store";
import {
  addAccommodationSystem,
  updateAccommodationSystem,
} from "../../../../store/features/optionalSystemsSlice";
import { AccommodationSystem } from "../../../../types/optionalSystems";
import { formatCurrency } from "../../../../utils/formatting";
import MESInlineEditor from "./MESInlineEditor";
import {
  selectBarracksStats,
  selectBrigStats,
  selectCabinSpaceStats,
  selectMultiEnvironmentSpaceStats
} from '../../../../store/selectors/derivedSelectors';

interface AccommodationSystemsProps {
  availableSystems: AccommodationSystem[];
  shipTonnage: number;
  techLevel: number;
  tlOverrideActive?: boolean;
}

const AccommodationSystems: React.FC<AccommodationSystemsProps> = ({
  availableSystems,
  shipTonnage,
  techLevel,
  tlOverrideActive = false,
}) => {
  const dispatch = useAppDispatch();
  const shipBuilderState = useAppSelector((state) => state.shipBuilder);

  // UI state
  const [cardQuantities, setCardQuantities] = React.useState<{
    [subtype: string]: number;
  }>({});
  const [cardPassengers, setCardPassengers] = React.useState<{
    [subtype: string]: number;
  }>({});
  const [cardDesignations, setCardDesignations] = React.useState<{
    [subtype: string]: string;
  }>({});

  // State for MES inline editor
  const [showMESEditor, setShowMESEditor] = useState(false);
  const [editingMESSystem, setEditingMESSystem] =
    useState<AccommodationSystem | null>(null);

  // Find the two systems
  const accelerationBench = availableSystems.find(
    (s) => s.subtype === "accelerationBench",
  );
  const accelerationSeat = availableSystems.find(
    (s) => s.subtype === "accelerationSeat",
  );
  const accelerationOptions = [accelerationBench, accelerationSeat].filter(
    (s): s is AccommodationSystem => !!s,
  );
  const [selectedAcceleration, setSelectedAcceleration] = useState<string>(
    () => {
      return (
        localStorage.getItem("selectedAccelerationType") || "accelerationBench"
      );
    },
  );
  useEffect(() => {
    localStorage.setItem("selectedAccelerationType", selectedAcceleration);
  }, [selectedAcceleration]);
  const selectedSystem = accelerationOptions.find(
    (s) => s.subtype === selectedAcceleration,
  );
  const [quantity, setQuantity] = useState<number>(1);

  // Find the stateroom systems
  const highStateroom = availableSystems.find(
    (s) => s.subtype === "highStateroom",
  );
  const luxuryStateroom = availableSystems.find(
    (s) => s.subtype === "luxuryStateroom",
  );
  const stateroomOptions = [highStateroom, luxuryStateroom].filter(
    (s): s is AccommodationSystem => !!s,
  );
  const [selectedStateroom, setSelectedStateroom] = useState<string>(() => {
    return localStorage.getItem("selectedStateroomType") || "highStateroom";
  });
  useEffect(() => {
    localStorage.setItem("selectedStateroomType", selectedStateroom);
  }, [selectedStateroom]);
  const selectedStateroomSystem = stateroomOptions.find(
    (s) => s.subtype === selectedStateroom,
  );
  const [stateroomQuantity, setStateroomQuantity] = useState<number>(1);

  // Use selectors for calculations
  const barracksStats = useAppSelector((state) =>
    selectBarracksStats(state, cardPassengers["barracks"] ?? 1)
  );
  const brigStats = useAppSelector((state) =>
    selectBrigStats(state, cardQuantities["brig"] ?? 1)
  );
  const cabinSpaceStats = useAppSelector((state) =>
    selectCabinSpaceStats(state, cardPassengers["cabinSpace"] ?? 1)
  );
  const multiEnvironmentSpaceStats = useAppSelector((state) =>
    selectMultiEnvironmentSpaceStats(state, 1)
  );

  const handleMESConfirm = (updatedSystem: AccommodationSystem) => {
    console.log(
      "MES DEBUG: handleMESConfirm called with system:",
      updatedSystem,
    );

    // Check if there's already a Multi-Environment Space system in the store
    const currentState = store.getState();
    const existingMES = currentState.optionalSystems.accommodations.find(
      (s) => s.subtype === "multiEnvironmentSpace",
    );

    if (existingMES) {
      // Update existing system
      console.log("MES DEBUG: updating existing system");
      dispatch(updateAccommodationSystem(updatedSystem));
    } else {
      // Add new system
      console.log("MES DEBUG: adding new system");
      dispatch(addAccommodationSystem(updatedSystem));
    }

    // Debug: Check the state after dispatch
    setTimeout(() => {
      const newState = store.getState();
      console.log(
        "MES DEBUG: Current optionalSystems state:",
        newState.optionalSystems,
      );
      console.log(
        "MES DEBUG: Current accommodations:",
        newState.optionalSystems.accommodations,
      );
    }, 100);

    setShowMESEditor(false);
    setEditingMESSystem(null);
  };

  const handleMESCancel = () => {
    setShowMESEditor(false);
    setEditingMESSystem(null);
  };

  const handleAddSystem = (system: AccommodationSystem) => {
    // Extract quantity from properties, defaulting to 1 if not present
    const quantity = (system.properties as any).quantity || 1;

    // Create a single system with the quantity stored in properties
    let newSystem: AccommodationSystem = {
      ...system,
      id: `${system.subtype}-${Date.now()}`,
      tonnage: system.tonnage * quantity, // Multiply tonnage by quantity
      cost: system.cost * quantity, // Multiply cost by quantity
      power: system.power * quantity, // Multiply power by quantity
      properties: {
        ...system.properties,
        quantity: quantity, // Store the quantity in properties for display
      },
    };
    dispatch(addAccommodationSystem(newSystem));
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Accommodation Systems</h2>
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-white dark:bg-gray-800">
        <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          Available Accommodation Systems
        </h3>
        {/* Card Grid for Available Accommodation Systems */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Acceleration Seating */}
          <div className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600">
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                Acceleration Seating
              </div>
              <select
                className="w-full mb-2 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 dark:bg-gray-600 dark:text-gray-100"
                value={selectedAcceleration}
                onChange={(e) => setSelectedAcceleration(e.target.value)}
              >
                {accelerationOptions.map((opt) => (
                  <option key={opt.subtype} value={opt.subtype}>
                    {opt.name}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                <div>
                  Cost:{" "}
                  {selectedSystem
                    ? `Cr${selectedSystem.cost.toLocaleString()}`
                    : ""}
                </div>
                <div>
                  Tonnage: {selectedSystem ? selectedSystem.tonnage : ""}
                </div>
                <div>Power: {selectedSystem ? selectedSystem.power : ""}</div>
              </div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Quantity
              </label>
              <input
                type="number"
                value={quantity}
                onChange={(e) =>
                  setQuantity(Math.max(1, Number(e.target.value)))
                }
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-600 dark:text-gray-100"
                min={1}
                step={1}
              />
            </div>
            <button
              className="mt-4 w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              onClick={() =>
                selectedSystem &&
                handleAddSystem({
                  ...selectedSystem,
                  properties: { ...selectedSystem.properties, quantity },
                })
              }
            >
              Add
            </button>
          </div>

          {/* Barracks */}
          {availableSystems.find((s) => s.subtype === "barracks") && (
            <div
              key="barracks"
              className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700"
            >
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Barracks</div>
                <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <div>Cost: {formatCurrency(barracksStats.cost)}</div>
                  <div>Tonnage: {barracksStats.tonnage}</div>
                  <div>Power: {barracksStats.power}</div>
                </div>
                {/* Designation selector */}
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Designation
                  </label>
                  <select
                    value={cardDesignations["barracks"] ?? "passenger"}
                    onChange={(e) =>
                      setCardDesignations((p) => ({
                        ...p,
                        barracks: e.target.value,
                      }))
                    }
                    className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-600 dark:text-gray-100"
                  >
                    <option value="passenger">Basic Passage</option>
                    <option value="crew">Crew Accommodation</option>
                  </select>
                </div>
                {/* Dynamic input fields */}
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {cardDesignations["barracks"] === "crew"
                      ? "Number of Crew"
                      : "Number of Basic Passengers"}
                  </label>
                  <input
                    type="number"
                    value={cardPassengers["barracks"] ?? 1}
                    onChange={(e) =>
                      setCardPassengers((p) => ({
                        ...p,
                        barracks: Math.max(1, Number(e.target.value)),
                      }))
                    }
                    className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-600 dark:text-gray-100"
                    min={1}
                    step={1}
                  />
                </div>
              </div>
              <button
                className="mt-2 px-3 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  const base = availableSystems.find(
                    (s) => s.subtype === "barracks",
                  );
                  if (!base) return;
                  let newSystem: AccommodationSystem = {
                    ...base,
                    id: `barracks-${Date.now()}`,
                    tonnage: barracksStats.tonnage,
                    cost: barracksStats.cost,
                    power: barracksStats.power,
                    properties: {
                      ...base.properties,
                      quantity: cardPassengers["barracks"] ?? 1,
                      designation: cardDesignations["barracks"] ?? "passenger",
                    },
                  };
                  dispatch(addAccommodationSystem(newSystem));
                }}
              >
                Add
              </button>
            </div>
          )}

          {/* Brig */}
          {availableSystems.find((s) => s.subtype === "brig") && (
            <div
              key="brig"
              className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700"
            >
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Brig</div>
                <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <div>Cost: {formatCurrency(brigStats.cost)}</div>
                  <div>Tonnage: {brigStats.tonnage}</div>
                  <div>Power: {brigStats.power}</div>
                </div>
                {/* Dynamic input fields */}
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    value={cardQuantities["brig"] ?? 1}
                    onChange={(e) =>
                      setCardQuantities((q) => ({
                        ...q,
                        brig: Math.max(1, Number(e.target.value)),
                      }))
                    }
                    className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-600 dark:text-gray-100"
                    min={1}
                    step={1}
                  />
                </div>
              </div>
              <button
                className="mt-2 px-3 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  const base = availableSystems.find(
                    (s) => s.subtype === "brig",
                  );
                  if (!base) return;
                  let newSystem: AccommodationSystem = {
                    ...base,
                    id: `brig-${Date.now()}`,
                    tonnage: brigStats.tonnage,
                    cost: brigStats.cost,
                    power: brigStats.power,
                    properties: {
                      ...base.properties,
                      quantity: cardQuantities["brig"] ?? 1,
                    },
                  };
                  dispatch(addAccommodationSystem(newSystem));
                }}
              >
                Add
              </button>
            </div>
          )}

          {/* Cabin Space */}
          {availableSystems.find((s) => s.subtype === "cabinSpace") && (
            <div
              key="cabinSpace"
              className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700"
            >
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Cabin Space
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <div>Cost: {formatCurrency(cabinSpaceStats.cost)}</div>
                  <div>Tonnage: {cabinSpaceStats.tonnage}</div>
                  <div>Power: {cabinSpaceStats.power}</div>
                </div>
                {/* Dynamic input fields */}
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Number of Passengers
                  </label>
                  <input
                    type="number"
                    value={cardPassengers["cabinSpace"] ?? 1}
                    onChange={(e) =>
                      setCardPassengers((p) => ({
                        ...p,
                        cabinSpace: Math.max(1, Number(e.target.value)),
                      }))
                    }
                    className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-600 dark:text-gray-100"
                    min={1}
                    step={1}
                  />
                </div>
              </div>
              <button
                className="mt-2 px-3 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  const base = availableSystems.find(
                    (s) => s.subtype === "cabinSpace",
                  );
                  if (!base) return;
                  let newSystem: AccommodationSystem = {
                    ...base,
                    id: `cabinSpace-${Date.now()}`,
                    tonnage: cabinSpaceStats.tonnage,
                    cost: cabinSpaceStats.cost,
                    power: cabinSpaceStats.power,
                    properties: {
                      ...base.properties,
                      quantity: cardPassengers["cabinSpace"] ?? 1,
                    },
                  };
                  dispatch(addAccommodationSystem(newSystem));
                }}
              >
                Add
              </button>
            </div>
          )}

          {/* Multi Environment Space */}
          {availableSystems.find(
            (s) => s.subtype === "multiEnvironmentSpace",
          ) && (
            <div
              key="multiEnvironmentSpace"
              className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700"
            >
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Multi Environment Space
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <div>
                    Cost: {formatCurrency(multiEnvironmentSpaceStats.cost)}
                  </div>
                  <div>Tonnage: {multiEnvironmentSpaceStats.tonnage}</div>
                  <div>Power: {multiEnvironmentSpaceStats.power}</div>
                </div>
              </div>
              <button
                className="mt-2 px-3 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  const base = availableSystems.find(
                    (s) => s.subtype === "multiEnvironmentSpace",
                  );
                  if (!base) return;
                  setEditingMESSystem(base);
                  setShowMESEditor(true);
                }}
              >
                Add
              </button>
            </div>
          )}

          {/* Staterooms */}
          {(availableSystems.find((s) => s.subtype === "highStateroom") ||
            availableSystems.find((s) => s.subtype === "luxuryStateroom")) && (
            <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700">
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Staterooms
                </div>
                <select
                  className="w-full mb-2 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 dark:bg-gray-600 dark:text-gray-100"
                  value={selectedStateroom}
                  onChange={(e) => setSelectedStateroom(e.target.value)}
                >
                  {stateroomOptions.map((opt) => (
                    <option key={opt.subtype} value={opt.subtype}>
                      {opt.name}
                    </option>
                  ))}
                </select>
                <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <div>
                    Cost:{" "}
                    {selectedStateroomSystem
                      ? formatCurrency(selectedStateroomSystem.cost)
                      : ""}
                  </div>
                  <div>
                    Tonnage:{" "}
                    {selectedStateroomSystem
                      ? selectedStateroomSystem.tonnage
                      : ""}
                  </div>
                  <div>
                    Power:{" "}
                    {selectedStateroomSystem
                      ? selectedStateroomSystem.power
                      : ""}
                  </div>
                </div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  value={stateroomQuantity}
                  onChange={(e) =>
                    setStateroomQuantity(Math.max(1, Number(e.target.value)))
                  }
                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-600 dark:text-gray-100"
                  min={1}
                  step={1}
                />
              </div>
              <button
                className="mt-4 w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={() =>
                  selectedStateroomSystem &&
                  handleAddSystem({
                    ...selectedStateroomSystem,
                    properties: {
                      ...selectedStateroomSystem.properties,
                      quantity: stateroomQuantity,
                    },
                  })
                }
              >
                Add
              </button>
            </div>
          )}
        </div>
      </div>
      {/* MES Inline Editor Modal */}
      {showMESEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-4">
              <MESInlineEditor
                availableSystems={availableSystems}
                shipBuilderState={shipBuilderState}
                onConfirm={handleMESConfirm}
                onCancel={handleMESCancel}
                editingSystem={editingMESSystem}
              />
            </div>
          </div>
        </div>
      )}

      {/* Installed Accommodation Systems section removed */}
    </div>
  );
};

export default AccommodationSystems;
